import { useCallback, useMemo, useRef } from "react"
import { INVOICE_FIELD_ORDER, VENDOR_FIELD_ORDER, CUSTOMER_FIELD_ORDER } from "../fieldConstants"
import type { Invoice, InvoiceFieldCategories } from "../types"

const createInitialFieldOrder = (): Record<string, number> => {
  const baseOrder = [
    ...INVOICE_FIELD_ORDER,
    ...VENDOR_FIELD_ORDER,
    ...CUSTOMER_FIELD_ORDER
  ]

  return baseOrder.reduce<Record<string, number>>((map, fieldName, index) => {
    map[fieldName.toLowerCase()] = index
    return map
  }, {})
}

export const useInvoiceFieldCategories = (invoice?: Invoice | null): InvoiceFieldCategories => {
  const initialFieldOrder = useMemo(createInitialFieldOrder, [])
  const fieldOrderRef = useRef<Record<string, number>>({ ...initialFieldOrder })
  const nextFieldOrderIndexRef = useRef<number>(Object.keys(initialFieldOrder).length)

  const ensureFieldOrder = useCallback((fieldName: string) => {
    const key = fieldName.toLowerCase()

    if (!(key in fieldOrderRef.current)) {
      fieldOrderRef.current[key] = nextFieldOrderIndexRef.current
      nextFieldOrderIndexRef.current += 1
    }

    return fieldOrderRef.current[key]
  }, [])

  const categorizedData = useMemo<InvoiceFieldCategories>(() => {
    const extractedFields = invoice?.extractedData ?? []

    const categories: InvoiceFieldCategories = {
      invoice: [],
      vendor: [],
      customer: [],
      lineItems: []
    }

    const sortedFields = [...extractedFields].sort((a, b) => {
      const orderA = ensureFieldOrder(a.field_name)
      const orderB = ensureFieldOrder(b.field_name)

      if (orderA !== orderB) {
        return orderA - orderB
      }

      const pageA = a.page_number || 1
      const pageB = b.page_number || 1
      if (pageA !== pageB) {
        return pageA - pageB
      }

      return a.field_name.localeCompare(b.field_name)
    })

    // These fields are shown in the "Currency & Amounts" section, not in generic fields
    const AMOUNTS_FIELDS = new Set(["subtotal", "tax_amount", "total_amount", "currency"])

    for (const field of sortedFields) {
      const fieldName = field.field_name.toLowerCase()
      // Skip fields rendered elsewhere
      if (fieldName === "unit_of_measure" || AMOUNTS_FIELDS.has(fieldName)) {
        continue
      }
      if (fieldName.startsWith("line_item_")) {
        categories.lineItems.push(field)
      } else if (INVOICE_FIELD_ORDER.includes(fieldName as typeof INVOICE_FIELD_ORDER[number])) {
        categories.invoice.push(field)
      } else if (VENDOR_FIELD_ORDER.includes(fieldName as typeof VENDOR_FIELD_ORDER[number])) {
        categories.vendor.push(field)
      } else if (CUSTOMER_FIELD_ORDER.includes(fieldName as typeof CUSTOMER_FIELD_ORDER[number])) {
        categories.customer.push(field)
      }
      // Unknown fields are intentionally dropped — Invoice Information is pinned
      // to the canonical INVOICE_FIELD_ORDER schema below.
    }

    // Pin the Invoice Information section to the canonical 7 fields in fixed
    // order. Synthesize empty placeholders for any field missing from
    // extractedData so the layout stays stable and the user can always edit.
    const existingByName = new Map<string, typeof categories.invoice[number]>()
    for (const f of categories.invoice) {
      existingByName.set(f.field_name.toLowerCase(), f)
    }
    categories.invoice = INVOICE_FIELD_ORDER.map((name) => {
      const existing = existingByName.get(name)
      if (existing) return existing
      return {
        id: `__missing_${name}`,
        field_name: name,
        field_value: "",
        confidence_score: 0,
        is_validated: false,
        is_corrected: false
      }
    })

    return categories
  }, [ensureFieldOrder, invoice?.extractedData])

  return categorizedData
}
